import { v4 as uuidv4 } from 'uuid';
import { forEach, isEmpty, isNil } from 'lodash';
import { isVisible } from './utils';
import { TagManagerEvents } from './events';
import { EventQueue } from './event-queue';

const dstm = window.dstm || {};
dstm.formsTracked = dstm.formsTracked || {};
dstm.formsInitiated = dstm.formsInitiated || {};
dstm.formsShown = dstm.formsShown || {};

export class FormEventHandler {
  #jquery;

  /** @var {TagManager} */
  #manager;

  #intersectionObserver;

  /** @var {EventQueue} */
  #submitEventQueue;

  /** @var {number} */
  #formSubmissionErrorTimeout;

  #formSubmissions = {};

  constructor(manager, jquery = window.jQuery) {
    this.#manager = manager;
    this.#jquery = jquery;
    this.#submitEventQueue = new EventQueue();

    const $ = this.#jquery;
    const handler = this;
    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        forEach(entries, (entry) => {
          const $form = $(entry.target);

          if (entry.intersectionRatio <= 0) {
            return;
          }

          if (isVisible(entry.target, entry.intersectionRatio)) {
            dstm.logger.debug('form intersecting more than 10%, track form shown if needed', {
              intersectingPercent: Math.floor(entry.intersectionRatio * 10000) / 100,
            });

            handler.trackFormShown($form);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      },
    );
  }

  handleFormSubmit($form) {
    const handler = this;
    const $ = this.#jquery;
    const manager = this.#manager;

    return function (event) {
      const onSubmitEventName = $form.data('dtm-on-submit-event-name');
      const onSubmitEventData = $form.data('dtm-on-submit-event-data') ?? {};
      const omitKeys = ($form.data('dtm-on-submit-omit-keys') || '').split(',');

      const formWrapper = $form.parent('.gform_wrapper');
      const formID = $form.data('formid');

      const formIsAjax = $form.attr('target') === `gform_ajax_frame_${formID}`;

      const hasPages = formWrapper.find('.gform_page').length > 0;
      const sourcePage = parseInt(
        formWrapper.find('input[name^="gform_source_page_number_"]').val(),
        10,
      );
      const targetPage = parseInt(
        formWrapper.find('input[name^="gform_target_page_number_"]').val(),
        10,
      );
      const isSubmit = targetPage === 0;
      const isNextSubmit = !isSubmit && targetPage > sourcePage;
      const isSave = $('#gform_save_' + formID).val() === '1';

      dstm.logger.debug('form submit', {
        formWrapper,
        formID,
        formIsAjax,
        hasPages,
        sourcePage,
        targetPage,
        isSubmit,
        isNextSubmit,
        isSave,
      });

      if (isSubmit) {
        const leadId = handler.getOrSetLeadId($form);
        if (!isNil(leadId)) {
          onSubmitEventData.leadId = leadId;
        }

        const submitFn = () => {
          if (handler.#formSubmissionErrorTimeout) {
            clearTimeout(handler.#formSubmissionErrorTimeout);
          }

          manager.emitEvent(TagManagerEvents.PRE_TRACK_EVENT, { event });
          manager.emitEvent(TagManagerEvents.PRE_TRACK_FORM_SUBMIT_EVENT, {
            event,
            $form,
            onSubmitEventName,
            onSubmitEventData,
          });

          // pass event through to adapters
          forEach(manager.adapters, (adapter) =>
            adapter.trackFormSubmit(event, $form, onSubmitEventName, onSubmitEventData, omitKeys),
          );

          handler.#formSubmissions[`form_${formID}`] = true;

          manager.emitEvent(TagManagerEvents.POST_TRACK_EVENT, { event });
          manager.emitEvent(TagManagerEvents.POST_TRACK_FORM_SUBMIT_EVENT, {
            event,
            $form,
            onSubmitEventName,
            onSubmitEventData,
          });
        };

        if (formIsAjax) {
          dstm.logger.debug(
            'ajax form detected, adding submit event to queue to trigger on successful submission',
            { queue: handler.#submitEventQueue },
          );
          handler.#submitEventQueue.add(() => submitFn());
        } else {
          submitFn();
        }
      }
    };
  }

  handleFormInitiation($form) {
    const manager = this.#manager;

    return function (event) {
      if ($form.data('initiated') || (dstm.formsInitiated[$form.attr('id')] ?? false)) {
        return;
      }

      const formInitiationEventData = $form.data('dtm-form-initiation-event-data') ?? {};

      dstm.logger.debug('form initiation', {
        event,
        formInitiationEventData,
      });

      manager.emitEvent(TagManagerEvents.PRE_TRACK_EVENT, { event });

      // pass event through to adapters
      forEach(manager.adapters, (adapter) => {
        adapter.trackFormEvent(event, $form, 'formInitiation', formInitiationEventData);
      });

      dstm.formsInitiated[$form.attr('id')] = true;
      $form.data('initiated', true);

      manager.emitEvent(TagManagerEvents.POST_TRACK_EVENT, { event });
    };
  }

  handleFormFieldInteraction($form) {
    const $ = this.#jquery;
    const manager = this.#manager;

    return function (event) {
      const $input = $(event.target);
      if ($input.data('interacted') ?? false) {
        return;
      }

      $input.data('interacted', true);
      const formFieldInteractionEventData = $form.data('dtm-form-interaction-event-data') ?? {};

      const $formField = $(event.target);
      const formFieldName = $formField.data('field-name') ?? event.target.id;

      dstm.logger.debug('form field interaction', {
        event,
        formFieldInteractionEventData,
      });

      manager.emitEvent(TagManagerEvents.PRE_TRACK_EVENT, { event });

      // pass event through to adapters
      forEach(manager.adapters, (adapter) => {
        adapter.trackFormEvent(event, $form, 'formFieldInteraction', {
          formFieldName,
          ...formFieldInteractionEventData,
        });
      });

      manager.emitEvent(TagManagerEvents.POST_TRACK_EVENT, { event });
    };
  }

  maybeHandleFormShown($form) {
    const el = $form.get(0);
    if (!el) {
      return;
    }

    dstm.logger.debug('maybe observing form element', {
      formsShown: dstm.formsShown,
      formElData: $form.data(),
      formIDAttr: $form.attr('id'),
    });

    if (!$form.data('shown') && !(dstm.formsShown[$form.attr('id')] ?? false)) {
      dstm.logger.debug('observing form element');
      this.#intersectionObserver.observe(el);
    }
  }

  maybeHandleFormSubmissionErrors($form) {
    const formId = $form.data('formid');
    const validationErrors = $form.find(`[id^="validation_message_${formId}_"]`);

    if (this.#formSubmissions[`form_${formId}`] === true) {
      return;
    }

    if (validationErrors.length > 0) {
      dstm.logger.debug('gform errors - clearing queued submit events', {
        queue: this.#submitEventQueue,
      });
      this.#submitEventQueue.clear();

      const $el = validationErrors.first();

      const onSubmitEventData = $form.data('dtm-on-submit-event-data') ?? {};
      const errorMessage = $el.text();

      const event = new Event('form-submission-error');

      // pass event through to adapters
      forEach(this.#manager.adapters, (adapter) => {
        adapter.trackFormEvent(event, $form, 'formSubmissionError', {
          formError: errorMessage,
          ...onSubmitEventData,
        });
      });
    }
  }

  setupFormFieldInteraction($form) {
    const handler = this;
    const formId = $form.data('formid');
    const $formInputs = $form.find(`[id^="input_${formId}_"]`);

    dstm.logger.debug('setup form field interaction', { $form, $formInputs });

    $formInputs.on('click keydown change input', function (event) {
      handler.handleFormInitiation($form).call(this, event);
      handler.handleFormFieldInteraction($form).call(this, event);
    });
  }

  maybeAddFormTracking($form) {
    const handler = this;

    dstm.logger.debug('maybe add form tracking', { $form });

    this.#formSubmissionErrorTimeout = setTimeout(() => {
      handler.maybeHandleFormSubmissionErrors($form);
    }, 300);
    handler.setupFormFieldInteraction($form);

    dstm.logger.debug('form tracking - changes', { id: $form.data('formid'), $form });
    $form.on('change', (e) => handler.handleFormInitiation($form).call(handler, e));

    dstm.logger.debug('form tracking - submissions', { id: $form.data('formid'), $form });
    $form.on('submit', (e) => handler.handleFormSubmit($form).call(handler, e));

    if ($form.data('tracked') || (dstm.formsTracked[$form.attr('id')] ?? false)) {
      return;
    }

    dstm.logger.debug('form tracking - shown', { id: $form.data('formid'), $form });
    handler.maybeHandleFormShown($form);

    const leadId = handler.getOrSetLeadId($form);
    dstm.logger.debug('form lead id', { leadId });

    $form.data('tracked', true);
    dstm.formsTracked[$form.attr('id')] = $form;
  }

  trackFormShown($form) {
    if ($form.data('shown') || (dstm.formsShown[$form.attr('id')] ?? false)) {
      dstm.logger.debug(`form [${$form.attr('id')}] already shown`);
      return;
    }

    const formShownEvent = new Event('form-shown');
    const formShownEventData = $form.data('dtm-form-shown-event-data') ?? {};

    this.#manager.emitEvent(TagManagerEvents.PRE_TRACK_EVENT, { event: formShownEvent });

    // pass event through to adapters
    forEach(this.#manager.adapters, (adapter) => {
      adapter.trackFormEvent(formShownEvent, $form, 'formShown', formShownEventData);
    });

    this.#manager.emitEvent(TagManagerEvents.POST_TRACK_EVENT, { event: formShownEvent });

    // unobserve so that shown event is only sent once
    dstm.formsShown[$form.attr('id')] = true;
    $form.data('shown', true);
  }

  trackGFForm(gfFormId) {
    const $ = this.#jquery;
    this.maybeAddFormTracking($(`#gform_${gfFormId}`));
  }

  processQueuedSubmitEventsIfNeeded() {
    dstm.logger.debug('processQueuedSubmitEventsIfNeeded', { queue: this.#submitEventQueue });
    this.#submitEventQueue.process();
  }

  getOrSetLeadId($form) {
    const $leadIdField = $form.find("input[data-field-name='lead-id']");

    let leadId = $leadIdField.val();
    if (isNil(leadId) || isEmpty(leadId)) {
      leadId = uuidv4();
      $leadIdField.val(leadId);
    }

    return leadId;
  }
}
